/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useState } from 'react';
import { SEO, serviceIcons, InfoIcon, CheckCircleIcon, CheckIcon, StoreBadges } from '../components/common';
import { colors, borderRadius, shadows, typography, transitions } from '../styles/theme';

type ServiceType = 'cekici' | 'vinc' | 'nakliye' | 'yol-yardim';

interface FormData {
  name: string;
  phone: string;
  email: string;
  city: string;
  serviceTypes: ServiceType[];
  experience: string;
  vehicleInfo: string;
  message: string;
}

const pageStyles = css`
  min-height: 100vh;
  background: linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.primary[800]} 50%, ${colors.secondary[600]} 100%);
  padding: 2rem 1rem;

  @media (min-width: 768px) {
    padding: 3rem 2rem;
  }
`;

const containerStyles = css`
  max-width: 900px;
  margin: 0 auto;
`;

const headerStyles = css`
  text-align: center;
  margin-bottom: 2rem;
  color: white;

  h1 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize['3xl']};
    font-weight: ${typography.fontWeight.extrabold};
    margin-bottom: 0.75rem;

    @media (min-width: 768px) {
      font-size: ${typography.fontSize['4xl']};
    }
  }

  p {
    font-size: ${typography.fontSize.lg};
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
  }
`;

const logoStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  font-weight: 800;
  font-size: 1.75rem;
  color: white;
`;

const logoIconStyles = css`
  width: 52px;
  height: 52px;
  border-radius: ${borderRadius.lg};
  box-shadow: ${shadows.lg};
`;

const storeBadgesStyles = css`
  margin-top: 1.75rem;
`;

const formContainerStyles = css`
  background: white;
  border-radius: ${borderRadius['2xl']};
  padding: 1.5rem;
  box-shadow: ${shadows['2xl']};

  @media (min-width: 768px) {
    padding: 3rem;
  }
`;

const serviceCardsStyles = css`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;

  @media (min-width: 640px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const serviceCardStyles = css`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1.5rem 1rem 1.25rem;
  background: white;
  border: 2px solid ${colors.neutral[200]};
  border-radius: ${borderRadius.xl};
  text-align: center;
  cursor: pointer;
  color: ${colors.text.secondary};
  transition: all ${transitions.fast};

  &:hover {
    border-color: ${colors.primary[300]};
    background: ${colors.primary[50]};
  }

  .icon svg {
    width: 40px;
    height: 40px;
  }

  .label {
    font-weight: ${typography.fontWeight.semibold};
    color: ${colors.text.primary};
    font-size: ${typography.fontSize.sm};
  }

  .check {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    width: 22px;
    height: 22px;
    border-radius: ${borderRadius.full};
    background: ${colors.primary[500]};
    color: white;
    display: none;
    align-items: center;
    justify-content: center;

    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const serviceCardActiveStyles = css`
  border-color: ${colors.primary[500]};
  background: ${colors.primary[50]};
  color: ${colors.primary[700]};
  box-shadow: 0 0 0 3px ${colors.primary[100]};

  .check {
    display: flex;
  }
`;

const sectionTitleStyles = css`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.text.primary};
  margin-bottom: 1rem;

  @media (min-width: 768px) {
    font-size: ${typography.fontSize.xl};
  }
`;

const formGridStyles = css`
  display: grid;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const inputGroupStyles = css`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label {
    font-weight: ${typography.fontWeight.medium};
    color: ${colors.text.primary};
    font-size: ${typography.fontSize.sm};
  }

  input, textarea, select {
    width: 100%;
    padding: 0.875rem 1rem;
    background: white;
    border: 2px solid ${colors.neutral[200]};
    border-radius: ${borderRadius.lg};
    font-size: ${typography.fontSize.base};
    transition: all ${transitions.fast};
    font-family: inherit;

    &:focus {
      outline: none;
      border-color: ${colors.primary[500]};
      box-shadow: 0 0 0 3px ${colors.primary[100]};
    }

    &::placeholder {
      color: ${colors.text.muted};
    }
  }

  textarea {
    min-height: 100px;
    resize: vertical;
  }
`;

const fullWidthStyles = css`
  @media (min-width: 640px) {
    grid-column: span 2;
  }
`;

const submitButtonStyles = css`
  width: 100%;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, ${colors.secondary[500]}, ${colors.secondary[600]});
  color: white;
  font-weight: ${typography.fontWeight.bold};
  font-size: ${typography.fontSize.lg};
  border: none;
  border-radius: ${borderRadius.xl};
  cursor: pointer;
  transition: all ${transitions.fast};
  box-shadow: 0 4px 12px ${colors.secondary[500]}40;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px ${colors.secondary[500]}50;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const submitHintStyles = css`
  text-align: center;
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.secondary};
  margin: 0.75rem 0 0;
`;

const successMessageStyles = css`
  text-align: center;
  padding: 2rem 0;

  .icon {
    width: 80px;
    height: 80px;
    margin: 0 auto 1.25rem;
    border-radius: ${borderRadius.full};
    background: ${colors.success[100]};
    color: ${colors.success[600]};
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 40px;
      height: 40px;
    }
  }

  h2 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize['2xl']};
    color: ${colors.success[600]};
    margin-bottom: 0.75rem;
  }

  p {
    color: ${colors.text.secondary};
    margin-bottom: 2rem;
  }
`;

const infoBoxStyles = css`
  background: ${colors.primary[50]};
  border: 1px solid ${colors.primary[200]};
  border-radius: ${borderRadius.lg};
  padding: 1rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;

  .icon {
    width: 20px;
    height: 20px;
    margin-top: 1px;
    flex-shrink: 0;
    color: ${colors.primary[700]};
  }

  p {
    font-size: ${typography.fontSize.sm};
    color: ${colors.primary[800]};
    line-height: 1.5;
    margin: 0;
  }
`;

const services: { id: ServiceType; label: string }[] = [
  { id: 'cekici', label: 'Çekici' },
  { id: 'vinc', label: 'Vinç' },
  { id: 'nakliye', label: 'Nakliye' },
  { id: 'yol-yardim', label: 'Yol Yardım' },
];

export default function DriverApplicationPage() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    phone: '',
    email: '',
    city: '',
    serviceTypes: [],
    experience: '',
    vehicleInfo: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const toggleService = (serviceId: ServiceType) => {
    setFormData(prev => ({
      ...prev,
      serviceTypes: prev.serviceTypes.includes(serviceId)
        ? prev.serviceTypes.filter(s => s !== serviceId)
        : [...prev.serviceTypes, serviceId],
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const serviceLabels = formData.serviceTypes.map(
      st => services.find(s => s.id === st)?.label || st
    ).join(', ');

    const waMessage = `*Yeni Sürücü Başvurusu*

*Ad Soyad:* ${formData.name}
*Telefon:* ${formData.phone}
*E-posta:* ${formData.email}
*Şehir:* ${formData.city}
*Hizmet Türleri:* ${serviceLabels}
*Deneyim:* ${formData.experience || '-'}
*Araç Bilgisi:* ${formData.vehicleInfo || '-'}
*Mesaj:* ${formData.message || '-'}`.trim();

    // WhatsApp ile gönderme
    const whatsappLink = `https://wa.me/905550103434?text=${encodeURIComponent(waMessage)}`;

    window.open(whatsappLink, '_blank');

    // Başarı durumu
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
    }, 1000);
  };

  if (isSuccess) {
    return (
      <>
        <SEO
          title="Başvuru Gönderildi - Sürücü Başvurusu"
          description="YolSepetiGO sürücü başvurunuz alındı."
        />
        <div css={pageStyles}>
          <div css={containerStyles}>
            <div css={formContainerStyles}>
              <div css={successMessageStyles}>
                <div className="icon"><CheckCircleIcon /></div>
                <h2>Başvurunuz Alındı!</h2>
                <p>
                  WhatsApp açılacaktır. Lütfen hazırlanan mesajı bize gönderin.<br />
                  En kısa sürede sizinle iletişime geçeceğiz.
                </p>
                <button
                  css={submitButtonStyles}
                  onClick={() => {
                    setIsSuccess(false);
                    setFormData({
                      name: '',
                      phone: '',
                      email: '',
                      city: '',
                      serviceTypes: [],
                      experience: '',
                      vehicleInfo: '',
                      message: '',
                    });
                  }}
                >
                  Yeni Başvuru Yap
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Sürücü Başvurusu"
        description="YolSepetiGO ailesine katılın! Çekici, vinç, nakliye veya yol yardım sürücüsü olarak başvurun."
        keywords={['sürücü başvurusu', 'çekici sürücüsü', 'vinç operatörü', 'nakliye şoförü', 'yol yardım']}
      />
      <div css={pageStyles}>
        <div css={containerStyles}>
          <header css={headerStyles}>
            <div css={logoStyles}>
              <img css={logoIconStyles} src="/goyazililogo.webp" alt="" width={52} height={52} />
              <span>YolSepetiGO</span>
            </div>
            <h1>Sürücü Olmak İstiyorum</h1>
            <p>YolSepetiGO ailesine katılın, birlikte büyüyelim!</p>

            <StoreBadges
              appStoreUrl="https://apps.apple.com"
              playStoreUrl="https://play.google.com"
              align="center"
              css={storeBadgesStyles}
            />
          </header>

          <div css={formContainerStyles}>
            <form onSubmit={handleSubmit}>
              <h2 css={sectionTitleStyles}>Hangi Hizmetleri Vermek İstiyorsunuz?</h2>
              <div css={serviceCardsStyles}>
                {services.map(service => {
                  const Icon = serviceIcons[service.id];
                  const selected = formData.serviceTypes.includes(service.id);
                  return (
                    <button
                      type="button"
                      key={service.id}
                      css={[serviceCardStyles, selected && serviceCardActiveStyles]}
                      onClick={() => toggleService(service.id)}
                      aria-pressed={selected}
                    >
                      <span className="check"><CheckIcon /></span>
                      <span className="icon"><Icon /></span>
                      <span className="label">{service.label}</span>
                    </button>
                  );
                })}
              </div>

              <div css={infoBoxStyles}>
                <InfoIcon className="icon" />
                <p>
                  Birden fazla hizmet türü seçebilirsiniz. Seçtiğiniz her hizmet için
                  uygun araç ve belgelere sahip olmanız gerekmektedir.
                </p>
              </div>

              <h2 css={sectionTitleStyles}>Kişisel Bilgiler</h2>
              <div css={formGridStyles}>
                <div css={inputGroupStyles}>
                  <label htmlFor="name">Ad Soyad *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Adınız ve soyadınız"
                    required
                  />
                </div>
                <div css={inputGroupStyles}>
                  <label htmlFor="phone">Telefon Numarası *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="05XX XXX XX XX"
                    required
                  />
                </div>
                <div css={inputGroupStyles}>
                  <label htmlFor="email">E-posta Adresi *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="ornek@email.com"
                    required
                  />
                </div>
                <div css={inputGroupStyles}>
                  <label htmlFor="city">Şehir *</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Hangi şehirde hizmet vermek istiyorsunuz?"
                    required
                  />
                </div>
              </div>

              <h2 css={sectionTitleStyles}>Deneyim ve Araç Bilgileri</h2>
              <div css={formGridStyles}>
                <div css={inputGroupStyles}>
                  <label htmlFor="experience">Deneyim Süresi</label>
                  <select
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                  >
                    <option value="">Seçiniz</option>
                    <option value="0-1">0-1 Yıl</option>
                    <option value="1-3">1-3 Yıl</option>
                    <option value="3-5">3-5 Yıl</option>
                    <option value="5-10">5-10 Yıl</option>
                    <option value="10+">10+ Yıl</option>
                  </select>
                </div>
                <div css={inputGroupStyles}>
                  <label htmlFor="vehicleInfo">Araç Bilgisi</label>
                  <input
                    type="text"
                    id="vehicleInfo"
                    name="vehicleInfo"
                    value={formData.vehicleInfo}
                    onChange={handleInputChange}
                    placeholder="Örn: Ford Cargo Çekici, 2020 model"
                  />
                </div>
                <div css={[inputGroupStyles, fullWidthStyles]}>
                  <label htmlFor="message">Eklemek İstediğiniz Notlar</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Kendinizi tanıtın, deneyimlerinizden bahsedin..."
                  />
                </div>
              </div>

              <button
                type="submit"
                css={submitButtonStyles}
                disabled={isSubmitting || formData.serviceTypes.length === 0}
              >
                {isSubmitting ? 'Gönderiliyor...' : 'Başvurumu Gönder'}
              </button>
              {formData.serviceTypes.length === 0 && (
                <p css={submitHintStyles}>Devam etmek için en az bir hizmet türü seçin.</p>
              )}
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
